<template>
  <div class="proposal-wizard">
    <div class="field">
      <label class="label">
        Component
      </label>
      <div class="control">
        <div class="select is-fullwidth">
          <select v-model="currentComponentKey">
            <option :value="componentKey" :key="component.name" v-for="(component, componentKey) in kolibriComponents">{{ component.name }}</option>
          </select>
        </div>
      </div>
    </div>

    <div class="field">
      <label class="label">Workflow</label>
      <div class="control">
        <div class="select is-fullwidth">
          <select v-model="currentWorkflowKey">
            <option :value="workflowKey" :key="workflowKey" v-for="(workflow, workflowKey) in currentComponent.workflows">{{ workflow.name }}</option>
          </select>
        </div>
      </div>
    </div>

    <div v-if="currentParams === null"  class="loader-wrapper">
      <div class="loader is-primary is-medium">
      </div>
    </div>
    <div v-else>
      <div class="field">
        <div class="control">
          <article class="message is-success">
            <div class="message-body">
              {{ currentWorkflow.description }}
              <br>
              <br>
              The current <strong>{{ currentParamName }}</strong> is <strong>{{ currentParamValue }}{{ currentWorkflow.modelDelimiter }}</strong>
            </div>
          </article>
        </div>
      </div>

      <article class="message is-dark">
        <div class="message-body">
          <div class="field">
            <label class="label has-text-weight-normal">
              Set new <strong>{{ currentParamName }}</strong>:
            </label>
            <div class="control">
              <div class="field has-addons">
                <p class="control">
                  <input :size="currentParamModel === null ? 10 : currentParamModel.toString().length + 5" :type="currentWorkflow.modelType" :min="currentWorkflow.modelMin" :max="currentWorkflow.modelMax" v-model="currentParamModel" class="input is-fullwidth" />
                </p>
                <p v-if="currentWorkflow.modelDelimiter !== null" class="control">
                  <a class="button is-static">
                    {{ currentWorkflow.modelDelimiter }}
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </article>


      <div class="field">
        <michelson-editor
          @editor-change="$emit('update-proposal', $event)"
          :michelson-raw="finalMichelson"
          :read-only="true"
        />
      </div>
    </div>
  </div>
</template>

<script>
  import { ovenProxyTemplateWithBreakGlass, ovenProxyTemplateWithoutBreakGlass } from "@/components/templates/ovenProxy"
  import { daoTemplateWithoutBreakGlass } from "@/components/templates/dao"
  import { tokenTemplateWithBreakGlass, tokenTemplateWithoutBreakGlass } from "@/components/templates/token"
  import {
    liquidityPoolRewardPercentTemplateWithBreakGlass,
    liquidityPoolRewardPercentTemplateWithoutBreakGlass
  } from "@/components/templates/liquidityPool"

  import MichelsonEditor from "@/components/MichelsonEditor"

  import BigNumber from "bignumber.js"
  import Decimal from "decimal.js"
  import {
    minterSetStabilityFeeTemplateWithBreakGlass,
    minterSetStabilityFeeTemplateWithoutBreakGlass
  } from "@/components/templates/minter/setStabilityFee";
  import {minterSetLiquidationFeePercentTemplateWithBreakGlass} from "@/components/templates/minter/setLiquidationFeePercent";
  import {
    minterUpdateCollateralizationPercentageTemplateWithBreakGlass,
    minterUpdateCollateralizationPercentageTemplateWithoutBreakGlass
  } from "@/components/templates/minter/setCollateralizationPercentage";

  import {
    savingsPoolUpdateInterestRateWithBreakGlass,
    savingsPoolUpdateInterestRateWithoutBreakGlass
  } from "@/components/templates/savingsPool";

  // TODO: Update this to false once the breakglass contracts are rotated out
  const USE_BREAKGLASS_CONTRACTS = true

  export default {
    name: "CreateProposalWizard",
    components: { MichelsonEditor },
    data(){
      const _this = this
      // TODO: Add these
      // - Oven Factory / setInitialDelegate
      // - Oven Factory / setGovernorContract
      // - Oven Registry / setGovernorContract
      // - kUSD Token / setGovernorContract
      // - kUSD Token / setDebtCeiling
      // - kUSD Token / setAdministrator
      // - Dev Fund / setDelegate
      // - Dev Fund / setAdministratorContract
      // - Dev Fund / setGovernorContract
      // - Stability Fund / setDelegate
      // - Stability Fund / setAdministratorContract
      // - Stability Fund / setGovernorContract
      // - Oracle / setGovernorContract
      // - Oracle / setMaxDataDelaySec
      // - LP / updateGovernorAddress
      // - LP / updateQuipuswapAddress
      // - LP / updateOvenRegistryAddress

      return {
        wizardMichelson: null,
        currentComponentKey: 'minter',
        currentWorkflowKey: 'adjustStabilityFee',
        currentParams: null,
        currentParamModel: null,
        currentParamName: null,
        currentParamValue: null,
        currentContract: null,
        initialParams: null,
        kolibriComponents: {
          minter: {
            name: "Kolibri Minter",
            contractKey: 'MINTER',
            breakGlassContract: _this.$store.contracts.BREAK_GLASS_CONTRACTS.MINTER,
            workflows: {
              adjustStabilityFee:  {
                name: "Update Stability Fee",
                description: "The Kolibri stability fee is the interest rate accrued per 60s period. Interest is accrued to the Stability Fund.",
                entrypoint: "%setStabilityFee",
                template: USE_BREAKGLASS_CONTRACTS ? minterSetStabilityFeeTemplateWithBreakGlass : minterSetStabilityFeeTemplateWithoutBreakGlass,
                paramName: 'stabilityFee',
                modelType: 'number',
                modelDelimiter: "%",
                async formatCurrentValue(currentValue){
                  const currentValueNoMantissa = new BigNumber(currentValue).dividedBy(new BigNumber(10).pow(18))
                  const currentValueDecimal = new Decimal(currentValueNoMantissa.toFixed(18)).times((60 * 24 * 365))
                  const E = new Decimal("2.7182818284590452353602874713526624977572470936999595749669676277240766303535475945713821785251664274")
                  const invertedLN = E.pow(currentValueDecimal)
                  return invertedLN.minus(1).times(100).toFixed(2)
                },
                async formatParamModel(paramModel){
                  // Convert percentages into 1m
                  const interestRate = new BigNumber(1).plus(new BigNumber(parseFloat(paramModel) / 100))

                  // TODO: Replace Math.log with something higher precision
                  const interestLog = new BigNumber(Math.log(interestRate.toNumber()))
                  const minutesPerYear = new BigNumber(60).times(24).times(365)
                  const mantissa = new BigNumber(10).pow(18)

                  return interestLog.dividedBy(minutesPerYear).times(mantissa).toFixed(0)
                },
                async args(){
                  const contractStorage = _this.currentContractStorage
                  return {
                    stabilityFee: contractStorage.stabilityFee.toFixed(),
                  }
                }
              },
              adjustLiquidationFeePercent: {
                name: "Update Liquidation Fee Percent",
                description: "The Liquidation Fee Percent is the liquidation fee required to liquidate an oven",
                entrypoint: "%setLiquidationFeePercent",
                template: USE_BREAKGLASS_CONTRACTS ? minterSetLiquidationFeePercentTemplateWithBreakGlass : minterSetStabilityFeeTemplateWithoutBreakGlass,
                paramName: 'liquidationFeePercent',
                modelType: 'number',
                modelDelimiter: "%",
                async formatCurrentValue(currentValue){
                  return new BigNumber(currentValue).dividedBy(Math.pow(10, 18)).times(100).toFixed(2)
                },
                async formatParamModel(paramModel){
                  return new BigNumber(parseFloat(paramModel)).dividedBy(100).times(new BigNumber(10).pow(18))
                },
                async args(){
                  const contractStorage = _this.currentContractStorage
                  return {
                    liquidationFeePercent: contractStorage.liquidationFeePercent.toFixed(),
                  }
                }
              },
              adjustCollateralizationPercentage: {
                name: "Update Collateralization Percentage",
                description: "The Collateralization Percentage is the required collateralization ratio for each oven. A 200% ratio would require $2 worth of XTZ to back $1 of kUSD, at 150% each kUSD would need to be backed by at least $1.50 in XTZ, etc etc..",
                entrypoint: "%setCollateralizationPercentage",
                template: USE_BREAKGLASS_CONTRACTS ? minterUpdateCollateralizationPercentageTemplateWithBreakGlass : minterUpdateCollateralizationPercentageTemplateWithoutBreakGlass,
                paramName: 'collateralizationPercentage',
                modelType: 'number',
                modelDelimiter: "%",
                async formatParamModel(paramModel){
                  return new BigNumber(parseFloat(paramModel)).times(new BigNumber(10).pow(18))
                },
                async formatCurrentValue(currentValue){
                  return new BigNumber(currentValue).dividedBy(Math.pow(10, 18)).toFixed(2)
                },
                async args(){
                  const contractStorage = _this.currentContractStorage
                  return {
                    collateralizationPercentage: contractStorage.collateralizationPercentage.toFixed(),
                  }
                }
              },
            },
          },
          ovenProxy: {
            name: "Kolibri Oven Proxy",
            contractKey: 'OVEN_PROXY',
            breakGlassContract: _this.$store.contracts.BREAK_GLASS_CONTRACTS.OVEN_PROXY,
            workflows: {
              setGovernorContract: {
                name: "Set Governor Contract",
                description: "The Governor Contract is the entity that can call privileged functions on the Kolibri Oven Proxy",
                entrypoint: "%setGovernorContract",
                template: USE_BREAKGLASS_CONTRACTS ? ovenProxyTemplateWithBreakGlass : ovenProxyTemplateWithoutBreakGlass,
                paramName: 'governorContractAddress',
                modelType: 'text',
                modelDelimiter: null,
                async formatCurrentValue(currentValue){
                  return currentValue
                },
                async formatParamModel(paramModel){
                  return paramModel
                },
                async args(){
                  const contractStorage = _this.currentContractStorage
                  return {
                    governorContractAddress: contractStorage.governorContractAddress
                  }
                }
              },
            },
          },
          DAO: {
            name: "Kolibri DAO",
            contractKey: 'DAO',
            workflows: {
              updateEscrowAmount: {
                name: "Update Escrow Amount",
                description: "The Escrow Amount is the amount of kDAO that one must offer to lock before submitting a proposal. If the vote does not hit a minimum threshold (minYayVotesPercentForEscrowReturn), the kDAO is given to the community fund and not returned.",
                entrypoint: "%setParameters",
                template: daoTemplateWithoutBreakGlass,
                paramName: 'escrowAmount',
                modelType: 'number',
                modelDelimiter: " kDAO",
                async formatCurrentValue(currentValue){
                  return new BigNumber(currentValue).dividedBy(new BigNumber(10).pow(18)).toFixed()
                },
                async formatParamModel(paramModel){
                  return new BigNumber(paramModel).multipliedBy(new BigNumber(10).pow(18)).toFixed()
                },
                async args(){
                  const contractStorage = _this.currentContractStorage
                  return {
                    escrowAmount: contractStorage.governanceParameters.escrowAmount.toFixed(),
                    voteDelayBlocks: contractStorage.governanceParameters.voteDelayBlocks.toFixed(),
                    voteLengthBlocks: contractStorage.governanceParameters.voteLengthBlocks.toFixed(),
                    minYayVotesPercentForEscrowReturn: contractStorage.governanceParameters.minYayVotesPercentForEscrowReturn.toFixed(),
                    blocksInTimelockForExecution: contractStorage.governanceParameters.blocksInTimelockForExecution.toFixed(),
                    blocksInTimelockForCancellation: contractStorage.governanceParameters.blocksInTimelockForCancellation.toFixed(),
                    percentageForSuperMajority: contractStorage.governanceParameters.percentageForSuperMajority.toFixed(),
                    quorumCapLower: contractStorage.governanceParameters.quorumCap.lower.toFixed(),
                    quorumCapUpper: contractStorage.governanceParameters.quorumCap.upper.toFixed(),
                  }
                }
              },
            }
          },
          token: {
            name: "kUSD Token",
            contractKey: 'TOKEN',
            breakGlassContract: _this.$store.contracts.BREAK_GLASS_CONTRACTS.TOKEN,
            workflows: {
              adjustDebtCeiling: {
                name: "Adjust the kUSD Debt Ceiling",
                description: "The debt ceiling is the maximum amount of kUSD that can exist (not counting outstanding interest). It's an important lever to be able to limit the amount of risk in the system, and ideally there should always be enough debt ceiling room to liquidate the largest ovens (though the liquidity pool helps hedge against kUSD availability constraints in black swan events).",
                entrypoint: "%setDebtCeiling",
                template: USE_BREAKGLASS_CONTRACTS ? tokenTemplateWithBreakGlass : tokenTemplateWithoutBreakGlass,
                paramName: 'debtCeiling',
                modelType: 'number',
                modelDelimiter: " kUSD",
                async formatParamModel(paramModel){
                  return new BigNumber(parseFloat(paramModel)).times(new BigNumber(10).pow(18)).toFixed(0)
                },
                async formatCurrentValue(currentValue){
                  return new BigNumber(currentValue).dividedBy(Math.pow(10, 18)).toFixed(0)
                },
                async args(){
                  const contractStorage = _this.currentContractStorage
                  return {
                    debtCeiling: contractStorage.debtCeiling.toFixed(0)
                  }
                }
              },
            },
          },
          liquidityPool: {
            name: "Kolibri Liquidity Pool",
            contractKey: 'LIQUIDITY_POOL',
            breakGlassContract: _this.$store.contracts.BREAK_GLASS_CONTRACTS.LIQUIDITY_POOL,
            workflows: {
              adjustRewardRate: {
                name: "Adjust the Liquidity Pool reward percentage",
                description: "The Liquidity Pool reward percentage is the amount of collateral that's delivered as part of the liquidation process to the user who triggered the liquidation through the pool.",
                entrypoint: "%updateRewardPercent",
                template: USE_BREAKGLASS_CONTRACTS ? liquidityPoolRewardPercentTemplateWithBreakGlass : liquidityPoolRewardPercentTemplateWithoutBreakGlass,
                paramName: 'rewardPercent',
                modelType: 'number',
                modelDelimiter: "%",
                async formatParamModel(paramModel){
                  return paramModel
                },
                async formatCurrentValue(currentValue){
                  return currentValue
                },
                async args(){
                  const contractStorage = _this.currentContractStorage
                  return {
                    rewardPercent: contractStorage.rewardPercent.toFixed(0)
                  }
                }
              },
            },
          },
          savingsPool: {
            name: "Kolibri Savings Rate Pool",
            contractKey: 'SAVINGS_POOL',
            breakGlassContract: _this.$store.contracts.BREAK_GLASS_CONTRACTS.SAVINGS_POOL,
            workflows: {
              adjustInterestRate: {
                name: "Adjust the Savings Rate Pool interest rate",
                description: "The interest rate is the amount of interest accrued yearly paid from the stability fund. Note: stability fee *must* be higher than this number to ensure the protocol remains solvent. ",
                entrypoint: "%setInterestRate",
                template: USE_BREAKGLASS_CONTRACTS ? savingsPoolUpdateInterestRateWithBreakGlass : savingsPoolUpdateInterestRateWithoutBreakGlass,
                paramName: 'interestRatePercentage',
                modelType: 'number',
                modelDelimiter: "%",
                async formatParamModel(paramModel){
                  // Convert percentages into 1m
                  const interestRate = new BigNumber(1).plus(new BigNumber(parseFloat(paramModel) / 100))

                  // TODO: Replace Math.log with something higher precision
                  const interestLog = new BigNumber(Math.log(interestRate.toNumber()))
                  const minutesPerYear = new BigNumber(60).times(24).times(365)
                  const mantissa = new BigNumber(10).pow(18)

                  return interestLog.dividedBy(minutesPerYear).times(mantissa).toFixed(0)
                },
                async formatCurrentValue(currentValue){
                  const currentValueNoMantissa = new BigNumber(currentValue).dividedBy(new BigNumber(10).pow(18))
                  const currentValueDecimal = new Decimal(currentValueNoMantissa.toFixed(18)).times((60 * 24 * 365))
                  const E = new Decimal("2.7182818284590452353602874713526624977572470936999595749669676277240766303535475945713821785251664274")
                  const invertedLN = E.pow(currentValueDecimal)
                  return invertedLN.minus(1).times(100).toFixed(2)
                },
                async args(){
                  const contractStorage = _this.currentContractStorage
                  return {
                    interestRatePercentage: contractStorage.interestRate.toFixed(0)
                  }
                }
              },
            },
          }
        }
      }
    },
    async mounted(){
      await this.updateContractInfo(this.currentWorkflowKey)
    },
    watch: {
      async currentComponentKey(newComponent){
        const newWorkflowKey = Object.keys(this.kolibriComponents[newComponent].workflows)[0]
        this.currentWorkflowKey = newWorkflowKey
        this.currentParams = null
        await this.updateContractInfo(newWorkflowKey)
      },
      async currentWorkflowKey(newValue){
        this.currentParams = null
        this.$emit('default-changed', false)
        await this.updateContractInfo(newValue)
      },
      async currentParamModel(newValue){
        const updatedValue = await this.currentWorkflow.formatParamModel(newValue)

        const initialValue = this.initialParams[this.currentParamName]
        if (updatedValue._isBigNumber && initialValue._isBigNumber){
          this.$emit('default-changed', !updatedValue.isEqualTo(initialValue))
        } else {
          let comparisonValue = updatedValue
          if (updatedValue._isBigNumber && initialValue._isBigNumber === undefined){
            comparisonValue = comparisonValue.toFixed()
          }
          this.$emit('default-changed', comparisonValue !== initialValue)
        }

        this.currentParams[this.currentParamName] = updatedValue
      }
    },
    methods: {
      async updateContractInfo(workflowKey){
        this.initialParams = null
        this.currentContract = await this.$store.tezosToolkit.contract.at(
          this.$store.contracts[this.currentComponent.contractKey]
        )
        this.currentContractStorage = await this.currentContract.storage()

        let contracts = { contract: this.$store.contracts[this.currentComponent.contractKey] }

        if (this.currentComponent.breakGlassContract !== undefined &&
            this.currentComponent.breakGlassContract !== null){
          contracts.breakGlassContract = this.currentComponent.breakGlassContract

          const breakGlassContract = await this.$store.tezosToolkit.contract.at(this.currentComponent.breakGlassContract)
          const breakGlassContractStorage = await breakGlassContract.storage()

          // Sanity check the breakglass to ensure things are wired correctly
          if (breakGlassContractStorage.targetAddress !== this.$store.contracts[this.currentComponent.contractKey]){
            alert(`The break glass contract at ${this.currentComponent.breakGlassContract} doesn't seem properly wired to ${breakGlassContractStorage.targetAddress}! Aborting.`)
            throw new Error("Invalid state detected!")
          }
        }

        this.currentParams = Object.assign({}, contracts, await this.kolibriComponents[this.currentComponentKey].workflows[workflowKey].args())
        this.initialParams = Object.assign({}, contracts, this.currentParams)

        this.currentParamName = this.kolibriComponents[this.currentComponentKey].workflows[workflowKey].paramName

        this.currentParamValue = await this.currentWorkflow.formatCurrentValue(this.currentParams[this.currentParamName])
        this.currentParamModel = this.currentParamValue
      },
    },
    computed: {
      currentComponent(){
        return this.kolibriComponents[this.currentComponentKey]
      },
      currentWorkflow(){
        return this.kolibriComponents[this.currentComponentKey].workflows[this.currentWorkflowKey]
      },
      finalMichelson(){
        if (this.currentParams === null){ return "" }
        return this.currentWorkflow.template(this.currentParams)
      }
    }
  }
</script>

<style lang="scss" type="text/scss">
  @import '../assets/sass/globals';
  .proposal-wizard{
    .params{
      border: 2px solid $light-grey;
    }
  }
</style>
